import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DollarSign, Award, BookOpen, PieChart, X } from "lucide-react";
import "./SchoolProfile.css";
import { schoolAPI } from "../services/api";
import DiversityChart from "./DiversityChart";
import { resolveCollegeLogo, COLLEGE_LOGO_FALLBACK } from "../lib/collegeLogos";
import ScholarshipsTab from "./ScholarshipsTab";

const toTitleCase = (value = "") =>
  value
    .replace(/\uFFFD/g, "")
    .toLowerCase()
    .split(/\s+/)
    .map((word) => (word ? word[0].toUpperCase() + word.slice(1) : ""))
    .join(" ")
    .trim();

const BASE_TABS = [
  { id: "colleges", label: "Top Colleges" },
  { id: "majors", label: "Top Majors" },
  { id: "academics", label: "Academics" },
  { id: "diversity", label: "Diversity" },
];

const SchoolProfile = ({
  schoolId,
  schoolSummary,
  isOpen,
  onClose,
  prefetchedProfile = null,
}) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Removed "detailsReady" blocking state - we want to show what we have immediately
  const [activeTab, setActiveTab] = useState(BASE_TABS[0].id);
  const [hasTabInteraction, setHasTabInteraction] = useState(false);
  const tabRowRef = useRef(null);
  const tabRefs = useRef({});
  const currentRequestRef = useRef(0);
  const detailsCacheRef = useRef({});
  const isAthletesMode =
    schoolSummary?.source === "athletes" || profile?.source === "athletes";
  const tabs = useMemo(() => {
    if (!isAthletesMode) return BASE_TABS;
    return [...BASE_TABS, { id: "scholarships", label: "Scholarships" }];
  }, [isAthletesMode]);

  // --- INITIALIZATION ---
  useEffect(() => {
    if (isOpen) {
      setActiveTab(tabs[0].id);
      setHasTabInteraction(false);
      
      // 1. INSTANT LOAD: Use whatever data we have right now
      if (prefetchedProfile) {
        detailsCacheRef.current[schoolId] = prefetchedProfile;
        setProfile(prefetchedProfile);
        setLoading(false);
      } else if (detailsCacheRef.current[schoolId]) {
        setProfile(detailsCacheRef.current[schoolId]);
        setLoading(false);
      } else if (schoolSummary) {
        // Hydrate with summary immediately so user sees Graduation Rates/etc instantly
        setProfile(schoolSummary);
        setLoading(true); // Still loading full details in background
      } else {
        setProfile(null);
        setLoading(true);
      }
    }
  }, [isOpen, prefetchedProfile, schoolSummary, schoolId]);

  // --- BACKGROUND FETCH ---
  useEffect(() => {
    if (isOpen && schoolId) {
      if (prefetchedProfile || detailsCacheRef.current[schoolId]) return;
      
      // Fetch full details silently
      setLoading(true);
      fetchSchoolProfile();
    }
  }, [isOpen, schoolId, schoolSummary, prefetchedProfile]);

  useEffect(() => {
    if (!tabRowRef.current) return;
    if (!hasTabInteraction && activeTab === tabs[0].id) {
      tabRowRef.current.scrollTo({ left: 0, behavior: "auto" });
      return;
    }

    const currentButton = tabRefs.current[activeTab];
    if (!currentButton) return;

    const row = tabRowRef.current;
    const padding = 16;
    const maxScroll = row.scrollWidth - row.clientWidth;
    let targetScroll = Math.max(0, currentButton.offsetLeft - padding);

    const currentIndex = tabs.findIndex((tab) => tab.id === activeTab);
    const nextTab = tabs[currentIndex + 1];
    if (nextTab) {
      const nextButton = tabRefs.current[nextTab.id];
      if (nextButton) {
        const nextRight = nextButton.offsetLeft + nextButton.offsetWidth + padding;
        if (nextRight > targetScroll + row.clientWidth) {
          targetScroll = Math.min(nextButton.offsetLeft - padding, maxScroll);
        }
      }
    }

    targetScroll = Math.min(targetScroll, maxScroll);

    row.scrollTo({
      left: targetScroll,
      behavior: hasTabInteraction ? "smooth" : "auto",
    });
  }, [activeTab, hasTabInteraction, tabs]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.classList.add("modal-open");
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.classList.remove("modal-open");
    };
  }, [isOpen, onClose]);

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    setHasTabInteraction(true);
  };

  const fetchSchoolProfile = async () => {
    const requestId = currentRequestRef.current + 1;
    currentRequestRef.current = requestId;
    setLoading(true);
    setError(null);

    const params = schoolSummary
      ? Object.fromEntries(
          Object.entries({
            city: schoolSummary.address_city,
            state: schoolSummary.address_state,
            name: schoolSummary.school_name,
            zipcode: schoolSummary.address_zipcode,
            dataset: schoolSummary.source,
          }).filter(([, value]) => value)
        )
      : {};

    const attempt = async () => {
      const response = await schoolAPI.getSchoolDetails(schoolId, params);
      if (!response.success) {
        throw new Error("Failed to get school details");
      }
      const normalizedProfile = { ...response.data };
      if (schoolSummary?.source === "athletes") {
        const percentFields = [
          "college_enrollment",
          "college_enrollment_rate",
          "four_year_matriculation_rate",
          "graduation_rate",
        ];
        percentFields.forEach((field) => {
          const value = normalizedProfile[field];
          if (typeof value === "number" && value <= 1.2) {
            normalizedProfile[field] = value * 100;
          }
        });
      }
      return normalizedProfile;
    };

    try {
      let result = null;
      let lastError = null;
      for (let i = 0; i < 2; i += 1) {
        try {
          result = await attempt();
          break;
        } catch (err) {
          lastError = err;
          await new Promise((resolve) => setTimeout(resolve, 200));
        }
      }
      if (result) {
        if (currentRequestRef.current !== requestId) return;
        
        // Merge with existing summary to prevent layout shift
        setProfile(prev => ({ ...prev, ...result }));
        setError(null);
        detailsCacheRef.current[schoolId] = result;
      } else {
        throw lastError || new Error("Failed to get school details");
      }
    } catch (err) {
      if (currentRequestRef.current === requestId) {
        // Even if detail fetch fails, we keep showing the summary data
        // Only show error if we have NOTHING to show
        if (!profile) setError(err.message || "Failed to get school details");
      }
    } finally {
      if (currentRequestRef.current === requestId) {
        setLoading(false);
      }
    }
  };

  const formatPercentage = (value) => {
    if (value === null || value === undefined) return "N/A";
    const num = Number(value);
    if (isNaN(num)) return "N/A";
    // Check if it's already a percentage (e.g. 85) or a decimal (e.g. 0.85)
    // If from summary it might be decimal, if from details might be different
    // Usually standardizing on 0-100 is best, but if we see < 1 we assume decimal
    if (num <= 1 && num > 0) return `${Math.round(num * 100)}%`;
    return `${Math.round(num)}%`;
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return "N/A";
    const num = Number(value);
    if (isNaN(num)) return "N/A";
    return Math.round(num).toLocaleString();
  };

  const getSchoolTypes = (data) => {
    const types = [];
    if (data?.is_public) types.push("Public");
    if (data?.is_charter) types.push("Charter");
    if (data?.is_boarding) types.push("Boarding");
    if (!data?.is_public && !data?.is_charter && data?.school_type) {
      types.push(data.school_type);
    } else if (!data?.is_public && !data?.is_charter) {
      types.push("Private");
    }
    return types;
  };

  const getGradeLevels = (data) => {
    const levels = [];
    if (data?.is_pk) levels.push("Pre-K");
    if (data?.is_elementary) levels.push("Elementary");
    if (data?.is_middle) levels.push("Middle");
    if (data?.is_high) levels.push("High");
    return levels;
  };

  const badgeTags = useMemo(() => {
    const source = profile ?? schoolSummary;
    if (!source) return [];
    const raw = [...getSchoolTypes(source), ...getGradeLevels(source)];
    return [...new Set(raw)].filter(
      (tag) => tag?.toLowerCase() !== "private"
    );
  }, [profile, schoolSummary]);

  const formatSnapgradRating = (value) => {
    if (value === null || value === undefined) return null;
    const num = Number(value);
    if (Number.isNaN(num) || num <= 0) return null;
    const clamped = Math.min(10, Math.max(0, num));
    const display = Number.isInteger(clamped) ? `${clamped}` : clamped.toFixed(1);
    return `${display}/10`;
  };

  const ratingValue = useMemo(() => {
    const raw =
      profile?.Snapgrad_rating ??
      profile?.snapgrad_rating ??
      schoolSummary?.Snapgrad_rating ??
      schoolSummary?.snapgrad_rating;

    return formatSnapgradRating(raw);
  }, [profile, schoolSummary]);

  if (!isOpen) return null;

  /* ---------------- HEADER ----------------- */
  const renderHeader = () => {
    const rawTitle = profile?.school_name ?? schoolSummary?.school_name ?? "School profile";
    const title = toTitleCase(rawTitle);
    const locationLine = [
      profile?.address_city ?? schoolSummary?.address_city,
      profile?.address_state ?? schoolSummary?.address_state,
    ]
      .filter(Boolean)
      .join(", ");

    return (
      <div className="relative bg-black text-white">
        <button
          onClick={onClose}
          className="absolute right-6 top-6 text-white/70 hover:text-white"
        >
          <X className="h-10 w-10" strokeWidth={2.5} />
        </button>

        <div className="px-6 py-6">
          <h1 className="text-[28px] md:text-[40px] font-unbounded">
            {title}
          </h1>
          {locationLine && (
            <p className="mt-2 text-white/70">{locationLine}</p>
          )}

          <div className="mt-4 flex items-center gap-3">
            <div className="h-[80px] w-[80px] rounded-full bg-[#F07639] flex items-center justify-center shrink-0">
              <span className="text-[22px] font-semibold">
                {ratingValue ?? "N/A"}
              </span>
            </div>
            <p className="text-sm text-white/70 max-w-md">
              Snapgrad rating based on academic readiness and outcomes.
            </p>
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            {badgeTags.map((tag) => (
              <span
                key={tag}
                className="border border-white/40 px-3 py-1 rounded-full text-xs"
              >
                {tag}
              </span>
            ))}
          </div>

        </div>
      </div>
    );
  };

  /* ---------------- ACADEMICS ----------------- */
  const renderAcademics = () => {
    if (!profile) return (
        <div className="flex items-center justify-center py-12">
          <span className="text-sm text-gray-600">Loading academics...</span>
        </div>
    );

    // NOTE: We no longer block on "detailsReady". 
    // We render what we have. Metrics that are null will show "N/A" until fetched.

    const performance = [
        { label: "Graduation rate", value: formatPercentage(profile.graduation_rate) },
        {
          label: "College enrollment",
          value: formatPercentage(
            profile.four_year_matriculation_rate ??
            profile.college_enrollment_rate ??
            profile.college_enrollment
          ),
        },
      {
        label: "Student teacher ratio",
        value: profile.student_teacher_ratio ? `${formatNumber(profile.student_teacher_ratio)}:1` : "N/A",
      },
      { label: "Overall grade", value: profile.grade_overall || "N/A" },
    ];

    const studentBody = [
      { label: "Total students", value: formatNumber(profile.total_students) },
      { label: "Female", value: formatPercentage(profile.gender_breakdown_female) },
      { label: "Male", value: formatPercentage(profile.gender_breakdown_male) },
      { label: "Religious affiliation", value: profile.religion_general || "Secular" },
    ];

    return (
      <div className="space-y-6">
        <section className="rounded-xl border bg-white p-6">
          <h2 className="text-xl font-unbounded">Academic Performance</h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            {performance.map(({ label, value }) => (
              <MetricTile key={label} label={label} value={value} />
            ))}
          </div>

          <div className="mt-6 border-t pt-6">
            <h3 className="text-lg font-unbounded">Student Body Overview</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              {studentBody.map(({ label, value }) => (
                <MetricTile key={label} label={label} value={value} subdued />
              ))}
            </div>
          </div>

          {profile.grades_offered && (
            <div className="mt-6 border-t pt-6">
              <p className="text-xs uppercase text-gray-500">Grades offered</p>
              <p className="mt-2 text-xl">{profile.grades_offered}</p>
            </div>
          )}

          {/* Only show Tuition section if data exists, otherwise it stays hidden until loaded */}
          {(profile.tuition || profile.pk_tuit) && (
            <div className="mt-6 border-t pt-6">
              <div className="flex items-center gap-3">
                <DollarSign className="h-5 w-5 text-[#F07639]" />
                <p className="text-lg font-unbounded">Tuition information</p>
              </div>
              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                {profile.tuition && (
                  <TuitionTile label="Annual tuition" amount={formatNumber(profile.tuition)} />
                )}
                {profile.pk_tuit && (
                  <TuitionTile label="Pre-K tuition" amount={formatNumber(profile.pk_tuit)} />
                )}
              </div>
            </div>
          )}
        </section>
      </div>
    );
  };

  /* ---------------- DIVERSITY ----------------- */
  const renderDiversity = () => {
    // If diversity data isn't loaded yet, show a skeleton or loading state for this tab only
    if (!profile?.diversity_breakdown && loading) {
       return (
        <section className="rounded-xl border bg-white p-6 h-64 flex items-center justify-center">
             <span className="text-gray-500 animate-pulse">Loading diversity data...</span>
        </section>
       );
    }
    
    if (!profile) return null;

    return (
      <section className="rounded-xl border bg-white p-6">
        <div className="flex items-center gap-3">
          <PieChart className="h-6 w-6 text-[#F07639]" />
          <h2 className="text-xl font-unbounded">Student Diversity</h2>
        </div>

        <div className="mt-6">
          <DiversityChart diversityData={profile.diversity_breakdown} />
        </div>
      </section>
    );
  };

  /* ---------------- TOP COLLEGES ----------------- */
  const renderColleges = () => {
    const colleges = profile?.top_colleges ?? [];
    const isLoading = colleges.length === 0 && loading;

    const resolveLogo = (college) =>
      resolveCollegeLogo(college, {
        fallback: COLLEGE_LOGO_FALLBACK,
      });

    return (
      <section className="rounded-xl border bg-white p-6">
        <div className="flex items-center gap-3">
          <Award className="h-6 w-6 text-[#F07639]" />
          <h2 className="text-xl font-unbounded">Top Colleges</h2>
        </div>
        <p className="text-sm text-gray-600 mt-2">
          The Colleges where the students of this school are most interested to attend after graduating.
        </p>

        {isLoading ? (
          <div className="mt-6 space-y-4 animate-pulse">
            {[0, 1, 2].map((index) => (
              <div
                key={`college-skeleton-${index}`}
                className="rounded-xl border bg-[#FFF8F4] p-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white flex items-center justify-center overflow-hidden p-2 border border-[#F0CB82] shrink-0">
                    <div className="w-6 h-6 rounded-full bg-[#EAD9CE]" />
                  </div>
                  <div className="space-y-2">
                    <div className="h-3 w-40 rounded bg-[#E7D9CF]" />
                    <div className="h-3 w-28 rounded bg-[#EFE1D7]" />
                  </div>
                </div>
                <div className="h-4 w-8 rounded bg-[#F2D7C4]" />
              </div>
            ))}
          </div>
        ) : colleges.length > 0 ? (
          <div className="mt-6 space-y-4">
            {colleges.slice(0, 10).map((college, index) => {
              const rawName = college?.name ?? "Unknown college";
              const ipeds = college?.ipeds;
              const { logoSrc, imgClassName, fallbackLogo } = resolveLogo(college);
              const shouldShowLogo = Boolean(logoSrc && logoSrc !== COLLEGE_LOGO_FALLBACK);

              return (
                <div
                  key={`college-${ipeds ?? rawName ?? index}`}
                  className="rounded-xl border bg-[#FFF8F4] p-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white flex items-center justify-center overflow-hidden p-2 border border-[#F0CB82] shrink-0">
                      {shouldShowLogo ? (
                        <img
                          src={logoSrc}
                          alt={`${rawName} logo`}
                          className={imgClassName}
                          onError={(event) => {
                            event.currentTarget.onerror = null;
                            event.currentTarget.src = fallbackLogo;
                          }}
                        />
                      ) : (
                        <span className="font-unbounded text-[14px] text-[#C46609] uppercase">
                          {(rawName || "?")
                            .split(/\s+/)
                            .filter(Boolean)
                            .map((w) => w[0]?.toUpperCase())
                            .join("")
                            .slice(0, 2) || "?"}
                        </span>
                      )}
                    </div>

                    <div>
                      <p className="font-semibold">{rawName}</p>
                      {ipeds && <p className="text-sm text-gray-600">Institution ID: {ipeds}</p>}
                    </div>
                  </div>

                  <div className="text-[#F07639] font-bold">#{index + 1}</div>
                </div>
              );
            })}
          </div>
        ) : (
          <EmptyState title="No college data" description="No college placement data available." />
        )}
      </section>
    );
  };
  /* ---------------- TOP MAJORS ----------------- */
  const renderMajors = () => {
    const majors = profile?.top_majors ?? [];

    const normalizeMajorName = (value) =>
      (value || "")
        .toString()
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");

    const uniqueMajors = (() => {
      const unique = [];
      const seen = new Set();
      for (const major of majors) {
        if (!major) continue;
        const name = normalizeMajorName(major?.name);
        if (!name || seen.has(name)) continue;
        seen.add(name);
        unique.push(major);
      }
      return unique;
    })();
    
    if (majors.length === 0 && loading) {
         return (
             <div className="py-12 text-center text-gray-500 animate-pulse">Loading top majors...</div>
         )
    }

    return (
      <section className="rounded-xl border bg-white p-6">
        <div className="flex items-center gap-3">
          <BookOpen className="h-6 w-6 text-[#F07639]" />
          <h2 className="text-xl font-unbounded">Top Majors</h2>
        </div>
        <p className="text-sm text-gray-600 mt-2">
          The Majors which the students of this school are most interested in pursuing.
        </p>

        {uniqueMajors.length > 0 ? (
          <div className="mt-6 space-y-4">
            {uniqueMajors.slice(0, 10).map((major, index) => (
              <div
                key={`major-${major.cip_code ?? major.name ?? index}`}
                className="rounded-xl border bg-white p-4 flex justify-between"
              >
                <div>
                  <p className="font-semibold">{major.name ?? "Unknown major"}</p>
                  {major.cip_code && (
                    <p className="text-sm text-gray-600">CIP code: {major.cip_code}</p>
                  )}
                </div>

                <div className="text-[#F07639] font-bold">#{index + 1}</div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState title="No major data" description="No major preference data available." />
        )}
      </section>
    );
  };

  /* ---------------- TAB SWITCH ----------------- */
  const renderTabContent = () => {
    switch (activeTab) {
      case "academics":
        return renderAcademics();
      case "diversity":
        return renderDiversity();
      case "colleges":
        return renderColleges();
      case "majors":
        return renderMajors();
      case "scholarships":
        return isAthletesMode ? (
          <ScholarshipsTab
            schoolId={schoolId}
            profile={profile}
            schoolSummary={schoolSummary}
          />
        ) : null;
      default:
        return renderAcademics();
    }
  };

  /* ---------------- BODY ----------------- */
  const renderBody = () => {
    if (error && !profile) {
      return (
        <div className="text-center py-10">
          <p className="text-red-600">{error}</p>
          <button onClick={fetchSchoolProfile} className="mt-4 px-4 py-2 bg-orange-600 text-white rounded">
            Try again
          </button>
        </div>
      );
    }

    if (!profile) {
      return (
        <div className="flex justify-center items-center h-full">
          Loading...
        </div>
      );
    }

    return (
      <div className="space-y-8">
        <div key={activeTab} className="school-profile-tab-panel">
          {renderTabContent()}
        </div>
        {loading && (
          <div className="flex justify-center items-center gap-2 text-sm text-gray-400 py-2">
              <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
              Updating details...
          </div>
        )}
      </div>
    );
  };

  /* ---------------- MODAL ----------------- */
  const modalContent = (
    <div className="school-profile-modal">
      <div className="school-profile-backdrop" onClick={onClose} />

      <div className="school-profile-content">
        {renderHeader()}

        <div className="bg-[#EFE7DF] px-6 py-4 school-profile-tab-shell">
          <div
            ref={tabRowRef}
            className="school-profile-tab-row flex gap-3 overflow-x-auto whitespace-nowrap flex-nowrap scrollbar-hide md:flex-wrap md:overflow-visible md:whitespace-normal md:justify-start"
          >
            {tabs.map((tab) => (
              <button
                ref={(el) => {
                  if (el) {
                    tabRefs.current[tab.id] = el;
                  } else {
                    delete tabRefs.current[tab.id];
                  }
                }}
                key={tab.id}
                onClick={() => handleTabClick(tab.id)}
                className={`school-profile-tab px-4 py-2 rounded-lg text-sm md:text-base shrink-0 ${
                  activeTab === tab.id ? "bg-white shadow text-black" : "text-gray-600"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6 bg-[#F9F4EE]">{renderBody()}</div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

/* ---------------- REUSABLE COMPONENTS ----------------- */

const MetricTile = ({ label, value, subdued = false }) => (
  <div className={`rounded-xl border bg-[#FFF8F4] p-4 text-center ${subdued ? "opacity-90" : ""}`}>
    <p className="text-xl font-bold">{value}</p>
    <p className="text-xs mt-2 text-gray-600 uppercase">{label}</p>
  </div>
);

const TuitionTile = ({ label, amount }) => (
  <div className="rounded-xl border bg-[#FFF8F4] p-4">
    <p className="text-xs uppercase text-gray-500">{label}</p>
    <p className="mt-2 text-xl">${amount}</p>
  </div>
);

const EmptyState = ({ title, description }) => (
  <div className="text-center py-12 border rounded-xl bg-[#FDF8F3]">
    <h3 className="text-xl font-unbounded">{title}</h3>
    <p className="text-gray-600 mt-2">{description}</p>
  </div>
);

export default SchoolProfile;
